import { useState, useEffect, useCallback } from "react";
import { Button, Table, Space, Popconfirm, message } from "antd";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import BranchModal from "../../components/BranchModal";
import { getBranches, createBranch, updateBranch, deleteBranch } from "../../api/Branch";
import Layout from '../../Layout'
import useAuthStore from "../../store/authStore";
import useTableColumnSettings from "../../hooks/useTableColumnSettings";
import { ColumnSettingButton } from "../../components/TableColumnSetting";

const BranchManager = () => {
    // Phân quyền thao tác: chỉ có quyền xem thì không hiện nút thêm/sửa/xóa
    const { isSuperAdmin, hasPermission } = useAuthStore();
    const canCreate = isSuperAdmin || hasPermission("branch_manager.create");
    const canEdit = isSuperAdmin || hasPermission("branch_manager.edit");
    const canDelete = isSuperAdmin || hasPermission("branch_manager.delete");
    const [open, setOpen] = useState(false);
    const [data, setData] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [editing, setEditing] = useState<any>(null);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);

    const fetchBranches = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getBranches({ page });
            if (res?.status) {
                const list = Array.isArray(res.data) ? res.data : res.data?.items || [];
                setData(list.map((item: any) => ({ ...item, key: item.id })));
                setTotal(res.data?.total || list.length);
            }
        } catch (error) {
            message.error("Không thể tải danh sách chi nhánh");
        } finally {
            setLoading(false);
        }
    }, [page]);

    useEffect(() => {
        fetchBranches();
    }, [fetchBranches]);

    const handleSubmit = async (values: any) => {
        try {
            if (editing) {
                const res = await updateBranch(editing.id, values);
                if (res?.status) {
                    message.success("Cập nhật chi nhánh thành công");
                    setEditing(null);
                    fetchBranches();
                    return true;
                } else {
                    message.error(res?.msg || "Cập nhật thất bại");
                    return false;
                }
            } else {
                const res = await createBranch(values);
                if (res?.status) {
                    message.success("Thêm chi nhánh thành công");
                    setEditing(null);
                    fetchBranches();
                    return true;
                } else {
                    message.error(res?.msg || "Thêm thất bại");
                    return false;
                }
            }
        } catch (error) {
            message.error("Có lỗi xảy ra");
            return false;
        }
    };

    const handleDelete = async (id: number) => {
        try {
            const res = await deleteBranch(id);
            if (res?.status) {
                message.success("Xóa chi nhánh thành công");
                fetchBranches();
            } else {
                message.error(res?.msg || "Xóa thất bại");
            }
        } catch (error) {
            message.error("Có lỗi xảy ra");
        }
    };

    const columns = [
        {
            title: "Tên chi nhánh",
            dataIndex: "name",
        },
        {
            key: "address",
            title: "Địa chỉ",
            render: (_: any, r: any) =>
                [r.address, r.ward_info?.full_name, r.province?.full_name].filter(Boolean).join(", "),
        },
        {
            title: "SĐT",
            dataIndex: "phone",
        },
        {
            key: "actions",
            title: "Hành động",
            fixed: "right" as const,
            lockVisible: true,
            render: (_: any, record: any) => (
                <Space>
                    {canEdit && (
                        <EditOutlined
                            onClick={() => {
                                setEditing(record);
                                setOpen(true);
                            }}
                            style={{ color: "#1677ff", cursor: "pointer" }}
                        />
                    )}

                    {canDelete && (
                        <Popconfirm
                            title="Xóa chi nhánh?"
                            onConfirm={() => handleDelete(record.id)}
                        >
                            <DeleteOutlined
                                style={{ color: "red", cursor: "pointer" }}
                            />
                        </Popconfirm>
                    )}
                </Space>
            ),
        },
    ];

    // Ẩn/hiện, sắp xếp, cố định, độ rộng cột — lưu theo tài khoản (extra_data)
    const {
        columns: tableColumns,
        components: tableComponents,
        tableLayout,
        scrollX,
        settingProps: columnSettingProps,
    } = useTableColumnSettings(
        "branch_list",
        (canEdit || canDelete) ? columns : columns.filter((c: any) => c.key !== "actions")
    );

    return (
        <Layout>
            <div className="flex justify-between items-center mb-4">
                <h1 className="text-[18px] font-bold text-000000e0">Quản lý chi nhánh</h1>
                <div className="flex items-center gap-2 mb-3">
                    {canCreate && (
                        <Button
                            type="primary"
                            onClick={() => {
                                setEditing(null);
                                setOpen(true);
                            }}
                        >
                            + Thêm chi nhánh
                        </Button>
                    )}
                    <ColumnSettingButton {...columnSettingProps} />
                </div>
            </div>

            <Table
                columns={tableColumns}
                components={tableComponents}
                tableLayout={tableLayout}
                dataSource={data}
                loading={loading}
                className="custom-ant-table"
                scroll={{ x: scrollX }}
                pagination={{
                    current: page,
                    total,
                    onChange: (p) => setPage(p),
                }}
            />

            <BranchModal
                open={open}
                onClose={() => setOpen(false)}
                onSubmit={handleSubmit}
                initialData={editing}
            />
        </Layout>
    );
};

export default BranchManager;